import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import { useState } from "react";
import prefecture from "util/prefecture";

const styles = {
  labelProps: {
    fontWeight: "bold",
    fontSize: { xs: 16, md: 18, lg: 20 },
    color: "var(--black)",
    lineHeight: "48px",
    height: "48px",
    padding: "0 18px",
    bgcolor: "var(--primary-500)",
    minWidth: { xs: "180px" },
    width: { xs: "180px" },
  },
  formContainerProps: {
    display: "flex",
    flexDirection: { lg: "row", sm: "column" },
    flexWrap: "wrap",
    gap: "16px",
    justifyContent: "center",
    alignItems: "center",
  },
  searchBtnSxProps: {
    backgroundColor: "var(--gray-500)",
    color: "var(--white)",
    width: "190px",
    height: "50px",
    fontSize: { xs: 18, lg: 25 },
    borderRadius: "9px",
    ":hover": {
      backgroundColor: "var(--gray-250)",
      boxShadow: "var(--shadow)",
    },
  },
};

interface ClientSearchFormProps {
  setTempQuery: (query: any) => void;
  onSubmit: () => void;
  tempQuery: { status_flag: number; [key: string]: any };
}

/**
 * ClientSearchForm component renders a form for searching clients.
 *
 * The form includes:
 * - A text field for entering client ID or name.
 * - A dropdown select for choosing a prefecture.
 * - A checkbox to filter only active clients.
 * - A search button to submit the form.
 *
 * The component uses Material-UI components such as Box, Typography, TextField, Select, MenuItem, FormControlLabel, Checkbox, and Button.
 *
 * @returns {Element} The rendered client search form.
 */
export default function ClientSearchForm({ setTempQuery, onSubmit, tempQuery }: ClientSearchFormProps) {
  const [selectedPrefecture, setSelectedPrefecture] = useState("");
  const handleSelectPrefecture = (e: any) => {
    const code = e.target.value;
    if (code) {
      setSelectedPrefecture(code);
      setTempQuery((prevQuery: any) => ({ ...prevQuery, pref_cd: code }));
    }
  };

  const handleClear = () => {
    if (selectedPrefecture) {
      setSelectedPrefecture("");
      setTempQuery((prevQuery: any) => ({ ...prevQuery, pref_cd: "" }));
    }
  };

  return (
    <>
      <Box sx={styles.formContainerProps}>
        <Box sx={{ ...styles.formContainerProps, width: { xs: "100%", md: "auto" } }}>
          <Box display="flex" gap="8px">
            <Typography {...styles.labelProps} textAlign={{ lg: "center" }}>
              得意先ID/得意先名
            </Typography>
            <TextField
              onChange={(e: any) => {
                setTempQuery((prevQuery: any) => ({
                  ...prevQuery,
                  client_cd_or_name: e.target.value,
                }));
              }}
              sx={{
                minWidth: "240px",
                height: "44px",
                p: 0,
                borderWidth: "1px",
                "& .MuiInputBase-root": { height: "48px", borderRadius: 0 },
              }}
              placeholder="得意先ID/得意先名"
            />
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Typography {...styles.labelProps} textAlign={{ lg: "center" }}>
              都道府県
            </Typography>
            <Select
              value={selectedPrefecture || ""}
              onChange={(e) => handleSelectPrefecture(e)}
              sx={{ minWidth: "240px", height: "48px", borderRadius: 0 }}
              displayEmpty
              renderValue={(value: string) => {
                if (!selectedPrefecture) {
                  return <Typography color="var(--gray-200)">都道府県</Typography>;
                }
                return prefecture.find((pref) => pref.code === value)?.name || "";
              }}
            >
              {prefecture.map((pref) => (
                <MenuItem key={pref.code} value={pref.code}>
                  {pref.name}
                </MenuItem>
              ))}
            </Select>
            {selectedPrefecture && (
              <IconButton onClick={handleClear} size="small">
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <FormControlLabel
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: { xs: 16, md: 18, lg: 20 },
              my: "auto",
              width: "200px",
            },
          }}
          control={
            <Checkbox
              color="secondary"
              checked={tempQuery.status_flag === 1}
              onChange={(e) => {
                setTempQuery((prevQuery: any) => ({
                  ...prevQuery,
                  status_flag: e.target.checked ? 1 : 0,
                }));
              }}
            />
          }
          label="有効のみ"
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          my: { lg: "40px", xs: "20px" },
        }}
      >
        <Button variant="contained" sx={styles.searchBtnSxProps} onClick={onSubmit}>
          検索
        </Button>
      </Box>
    </>
  );
}
